import { Col, Row } from 'react-bootstrap';
import { AiFillPrinter } from 'react-icons/ai';
import { IoIosMail, IoLogoWhatsapp } from 'react-icons/io';
import { MdWifiCalling3 } from 'react-icons/md';
import { SlUserFollow, SlUserFollowing } from 'react-icons/sl';
import React, { useState } from 'react';
import { BsDot } from 'react-icons/bs';
import { RiShareForwardFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BreadCrumb from '../../Component/BreadCrumb';
import { restrictOnlyUserActions } from '../../Helper/restrictActions';
import { capitalizeFirstLetter, imageUrl } from '../../config/apiUrl';
import { office } from '../../constant/imagePath';
import CompanyOrAgentEmailModal from '../../modals/CompanyOrAgentEmailModal';
import { useFollowUnFollowCompanyOrAgentMutation } from '../../store/apiSlice/apiSlice';
import { updateUser } from '../../store/auth/authSlice';
import BannerImageAd from '../BannerImageAd';
import Button from '../Button/Button';
import ShareModal from '../ShareModal/ShareModal';
import ShowMoreShowLessText from '../ShowMoreShowLess/ShowMoreShowLessText';
import Tooltip from '../Tooltip';
import MapMain from '../maps/MapMain';
import classes from './CompanyIntroSection.module.css';

const CompanyIntroSection = ({
  bannerType,
  className = '',
  data,
  t,
  verticalBanner = '',
  verticalLink = ''
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedLanguage } = useSelector((state) => state.languageReducer);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLogin, user } = useSelector((state) => state.authReducer);
  const [onFollow, { isLoading }] = useFollowUnFollowCompanyOrAgentMutation();

  const isFollowed = user?.companyFollowing?.includes(data?._id);

  const handleAction = () => {
    const isAllowedActions = restrictOnlyUserActions(user?.role);
    if (!isLogin) {
      navigate('/login');
    } else if (isAllowedActions) {
      return true;
    }
  };

  async function handleFollow() {
    if (!handleAction()) return;
    const res = await onFollow(data?._id);
    if (res?.data) {
      dispatch(updateUser(res?.data?.data));
      toast.success(
        `${t('company.companyIntroSection.company')} ${
          isFollowed
            ? t('company.companyIntroSection.unFollowed')
            : t('company.companyIntroSection.followed')
        } ${t('company.companyIntroSection.successfully')}`
      );
    }
  }

  return (
    <section className={className}>
      <Row className={classes.topRow}>
        <Col sm={12} md={6}>
          <div className={classes?.breadCrumbContainer}>
            <BreadCrumb
              data={[
                { title: 'Home', path: '/' },
                { title: 'Companies', path: '/agents?tab=company' },
                {
                  title: capitalizeFirstLetter(data?.companyName?.[selectedLanguage]),
                  active: true
                }
              ]}
            />
            <p>{capitalizeFirstLetter(data?.companyName?.[selectedLanguage])}</p>
          </div>
        </Col>
        <Col sm={12} md={6} className={classes?.shareContainer}>
          {/* Share + print */}
          <div className={classes?.shareLogoContainer}>
            <div>
              <AiFillPrinter fill="#a4a4a4" size={25} />
            </div>
            <button
              className="btnWrapper"
              type="button"
              onClick={() => setIsShareModalOpen(true)}
              aria-label="Share">
              <RiShareForwardFill fill="#a4a4a4" size={25} />
            </button>
          </div>
        </Col>
      </Row>
      <Row className={classes.gap}>
        {/* Basic Intro */}
        <Col sm={12} md={4} lg={3}>
          <div className={classes?.agentIntroContainer}>
            <div className={classes?.profileImageContainer}>
              <img alt="company" src={imageUrl(data?.photo)} />
            </div>
            {/* map */}
            <div className={classes?.companyLogoAndShareContainer}>
              <div className={classes?.companyInfo}>
                <h6>{data?.companyName?.[selectedLanguage]}</h6>
                <h5>{data?.companyType?.[selectedLanguage]}</h5>
              </div>
            </div>

            {/* Follow Btn */}
            <Button
              disabled={isLoading}
              className={classes?.followBtnContainer}
              onClick={handleFollow}>
              {(() => {
                if (isLoading) return <></>;
                if (isFollowed) {
                  return <SlUserFollowing fill="var(--primary-clr)" size={18} />;
                }

                return <SlUserFollow fill="var(--primary-clr)" size={18} />;
              })()}
              {(() => {
                if (isLoading) return t('company.followBtnLoading');
                if (isFollowed) return t('company.following');

                return t('company.follow');
              })()}
            </Button>

            <div className={classes.socialIconsWrap1}>
              <div className={classes.socialIcons1}>
                <Tooltip text={t('company.tooltips.call')} href={`tel:${data?.phoneNumber}`}>
                  <MdWifiCalling3 fill="var(--primary-clr)" size={30} />
                </Tooltip>
              </div>
              <button
                type="button"
                className={[classes.socialIcons1, classes.midIcon, 'btnWrapper'].join(' ')}
                onClick={() => setIsModalOpen(true)}
                aria-label="Email">
                <Tooltip text={t('company.tooltips.email')}>
                  <IoIosMail fill="var(--primary-clr)" size={32} />
                </Tooltip>
              </button>
              <div className={classes.socialIcons1}>
                <Tooltip
                  text={t('company.tooltips.whatsapp')}
                  href={`https://wa.me/${data?.whatsapp}`}
                  target={'_blank'}>
                  <IoLogoWhatsapp fill="var(--primary-clr)" size={30} />
                </Tooltip>
              </div>
            </div>
          </div>
          {/* Follow Btn */}
        </Col>
        {/* About Me */}
        <Col sm={12} md={8} lg={7}>
          <div className={classes?.aboutMeContainer}>
            {/* company logo + share */}
            <div className={classes?.mapContainer}>
              <MapMain
                showLandMarks={false}
                markerIcon={office}
                mapClass={classes?.mapClass}
                zoom={18}
                options={mapOptions}
                location={{
                  lng: data?.location?.coordinates[0],
                  lat: data?.location?.coordinates[1]
                }}
              />
            </div>

            {/* Agent Details */}
            <div className={classes?.agentDetailsContainer}>
              <Row className={classes?.agentDetails}>
                <Col md={4}>{t('company.employees')} </Col>
                <Col md={8}>
                  <span>{data?.agents?.length}</span> {t('company.agents')}
                </Col>
              </Row>
              <Row className={classes?.agentDetails}>
                <Col md={4}>{t('company.weSpeak')} </Col>
                <Col md={8}>{data?.language?.map((e) => e?.[selectedLanguage])?.join(', ')}</Col>
              </Row>
              <Row className={classes?.agentDetails}>
                <Col md={4}>{t('company.properties')} </Col>
                <Col md={8}>
                  <div className={classes?.propertyMain}>
                    <div className={classes.propertyItem}>
                      <span>({data?.buyProperties ?? 0})</span> {t('company.forBuy')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.rentProperties ?? 0})</span> {t('company.forRent')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.totalProjects ?? 0})</span> {t('company.projects')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.totalEvents ?? 0})</span> {t('company.events')}
                    </div>
                  </div>
                </Col>
              </Row>
              <div className={classes?.agentDetails}>
                <p>{`${t('company.serviceAreas')}:`} </p>
                <p>{data?.serviceAreas?.[selectedLanguage]}</p>
              </div>
            </div>

            {/* Description */}
            <div className={classes?.descriptionContainer}>
              <h5>
                {t('company.aboutMe')} {data?.companyName?.[selectedLanguage]}
              </h5>
              <ShowMoreShowLessText text={data?.description?.[selectedLanguage]} visibility={200} />
            </div>
          </div>
        </Col>

        {/* Banner Ad */}
        <Col sm={12} md={12} lg={2}>
          <div hidden={!verticalLink} className={classes?.bannerAdContainer}>
            <BannerImageAd
              bannerType={bannerType}
              link={verticalLink}
              image={verticalBanner}
              className={classes.banner}
            />
          </div>
        </Col>
      </Row>

      {isShareModalOpen && <ShareModal show={isShareModalOpen} setShow={setIsShareModalOpen} />}
      {isModalOpen && (
        <CompanyOrAgentEmailModal
          t={t}
          show={isModalOpen}
          setShow={setIsModalOpen}
          type="company"
          data={data}
        />
      )}
    </section>
  );
};

export default CompanyIntroSection;

// const _d = `Lorem ipsum dolor sit amet consectetur adipisicing elit. Maxime mollitia,
// molestiae quas vel sint commodi repudiandae consequuntur voluptatum laborum
// numquam blanditiis harum quisquam eius sed odit fugiat iusto fuga praesentium
// optio, eaque rerum!
// Lorem ipsum dolor sit amet consectetur adipisicing elit. Maxime mollitia,
// molestiae quas vel sint commodi repudiandae consequuntur voluptatum laborum
// numquam blanditiis harum quisquam eius sed odit fugiat iusto fuga praesentium
// optio, eaque rerum!
// Lorem ipsum dolor sit amet consectetur adipisicing elit. Maxime mollitia,
// molestiae quas vel sint commodi repudiandae consequuntur voluptatum laborum
// numquam blanditiis harum quisquam eius sed odit fugiat iusto fuga praesentium
// optio, eaque rerum!

// `;

const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: false,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels'
    }
  ]
};
